import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { siteMeta, stats, testimonials } from "@/mocks/site";
import TipForm from "./components/TipForm";

const positioning = {
  do: [
    "整理官方公告、节目正片、公开声明等可查证信息",
    "把事件按发生顺序排成可核对的时间线",
    "明确区分「已知事实」与「存疑推测」",
    "在出现新公开回应后及时补充更新",
  ],
  dont: [
    "采用无法核实的私信爆料与匿名截图",
    "对当事人进行人身攻击或道德审判",
    "把推测包装成结论来吸引点击",
    "公开任何人的私人联系方式与住址信息",
  ],
};

const philosophy = [
  {
    icon: "ri-compass-3-line",
    title: "把复杂的事情讲简单",
    text: "一条时间线胜过十段情绪表达。我们相信读者有能力自己做判断，前提是信息给得足够完整、顺序足够清楚。",
  },
  {
    icon: "ri-links-line",
    title: "只对公开信息负责",
    text: "所有引用都来自公开渠道，并尽量标注出处。我们不替任何一方背书，也不参与舆论站队。",
  },
  {
    icon: "ri-refresh-line",
    title: "允许自己被更新",
    text: "事件会发展，结论会变化。当新的公开信息出现，我们会直接改时间线，而不是删掉旧内容假装没发生过。",
  },
];

const principles = [
  { title: "来源公开可查", text: "每一个关键节点都对应一份可访问的公开内容，读者可以自行核对。" },
  { title: "事实与推测分区", text: "正文中单独标注不确定的部分，避免读者把推测当成结论。" },
  { title: "不预设立场", text: "在事实不足时，我们宁可停在「目前已知到哪一步」，也不替谁下判断。" },
  { title: "保护当事人隐私", text: "即使话题涉及公众人物，我们也不会公开其私人联系方式与行踪信息。" },
];

export default function About() {
  useSeo({
    title: "关于我们 - 独家吃瓜的内容原则与联系方式",
    description:
      "独家吃瓜是一个以时间线为核心的事件复盘内容站。这里介绍我们的品牌定位、发展理念、内容原则与联系方式，也提供线索投递入口。",
    keywords: "关于独家吃瓜,独家吃瓜内容原则,独家吃瓜团队,独家吃瓜联系方式,独家吃瓜复盘",
    canonicalPath: "/about",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          name: "关于独家吃瓜",
          description: "介绍独家吃瓜的品牌定位、发展理念与内容原则。",
        },
        {
          "@type": "Organization",
          name: siteMeta.name,
          email: siteMeta.email,
          description: siteMeta.intro,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "/" },
            { "@type": "ListItem", position: 2, name: "关于我们", item: "/about" },
          ],
        },
      ],
    },
  });

  return (
    <>
      <SiteHeader />
      <main>
        <div className="border-b border-background-200 bg-background-100">
          <div className="container-x py-8 md:py-12">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <p className="mt-4 text-xs font-medium tracking-[0.2em] text-primary-600">ABOUT / 关于我们</p>
            <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              我们只做一件事：把来龙去脉讲清楚
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-600 md:text-base md:leading-8">
              {siteMeta.intro}
            </p>
          </div>
        </div>

        <section className="container-x py-14 md:py-20">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                我们是谁
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-8 text-foreground-700 md:text-base md:leading-9">
                <p>
                  独家吃瓜由一支小规模内容团队在运营，成员来自媒体、影视宣发和演出票务三个方向。我们每天都在做同一件事：把一件被讨论得沸沸扬扬的事，拆成一条可以按顺序读完的线。
                </p>
                <p>
                  我们不做标题党，也不靠情绪推进阅读。一个事件被收录进档案之前，我们会先确认三件事：关键节点是否都有公开出处、各方回应是否已经收齐、以及正文里有没有把推测写成了结论。
                </p>
                <p>
                  这也是我们选择「时间线优先」的原因。结论会随着新信息变化，但顺序不会——只要顺序是对的，读者就能自己看清楚哪一步出了问题。
                </p>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="h-64 w-full overflow-hidden rounded-lg bg-background-200 md:h-80">
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20editorial%20desk%20with%20open%20notebook%20timeline%20sketch%20and%20ceramic%20coffee%20cup%2C%20soft%20morning%20light%20through%20window%2C%20no%20people%2C%20no%20readable%20text%2C%20moody%20still%20life%20photography&width=800&height=600&seq=gua-about-01&orientation=landscape"
                  alt="编辑台与时间线笔记的暖色静物图"
                  title="独家吃瓜 内容团队工作场景"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="container-x py-14 md:py-20">
            <div className="max-w-2xl">
              <p className="text-xs font-medium tracking-[0.2em] text-secondary-600">POSITION / 网站定位</p>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                我们做什么，不做什么
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                边界清楚，读起来才放心。下面这两组内容，是我们每天都要对着检查一遍的标准。
              </p>
            </div>

            <div className="mt-9 grid grid-cols-1 gap-5 md:grid-cols-2">
              <div className="rounded-lg border border-secondary-200 bg-background-50 p-6">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-secondary-800">
                  <i className="ri-checkbox-circle-line text-lg"></i>
                  我们会做的
                </h3>
                <ul className="mt-5 space-y-3.5">
                  {positioning.do.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm leading-7 text-foreground-700">
                      <i className="ri-check-line mt-1 shrink-0 text-secondary-600"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-lg border border-primary-200 bg-background-50 p-6">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-primary-700">
                  <i className="ri-close-circle-line text-lg"></i>
                  我们不会做的
                </h3>
                <ul className="mt-5 space-y-3.5">
                  {positioning.dont.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm leading-7 text-foreground-700">
                      <i className="ri-close-line mt-1 shrink-0 text-primary-600"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="container-x py-14 md:py-20">
          <div className="max-w-2xl">
            <p className="text-xs font-medium tracking-[0.2em] text-primary-600">PHILOSOPHY / 发展理念</p>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              支撑我们做下去的三件事
            </h2>
          </div>
          <div className="mt-9 grid grid-cols-1 gap-5 md:grid-cols-3">
            {philosophy.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-7 text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="container-x py-14 md:py-20">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
              <div className="lg:col-span-4">
                <p className="text-xs font-medium tracking-[0.2em] text-secondary-600">PRINCIPLES / 内容原则</p>
                <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  四条不能让步的规则
                </h2>
                <p className="mt-4 text-sm leading-7 text-foreground-600">
                  这四条不是口号，是每一篇档案发布前都要走一遍的检查清单。
                </p>
              </div>
              <div className="lg:col-span-8">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {principles.map((item, i) => (
                    <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                      <span className="font-heading text-2xl font-black text-background-300">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <h3 className="mt-2 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                      <p className="mt-2 text-sm leading-7 text-foreground-600">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="container-x py-14 md:py-20">
          <div className="grid grid-cols-2 gap-6 rounded-lg border border-background-200 bg-background-50 px-6 py-8 md:grid-cols-4 md:px-10">
            {stats.map((item) => (
              <div key={item.label}>
                <p className="font-heading text-2xl font-bold text-primary-600 md:text-3xl">{item.value}</p>
                <p className="mt-2 text-xs leading-5 text-foreground-600">{item.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-14">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">用户口碑</h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
              这些反馈来自长期阅读的读者，也是我们判断内容方向是否走对的重要参照。
            </p>
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {testimonials.map((item) => (
                <figure key={item.name} className="rounded-lg border border-background-200 bg-background-100 p-6">
                  <i className="ri-double-quotes-l text-2xl text-primary-500"></i>
                  <blockquote className="mt-3 text-sm leading-8 text-foreground-700">{item.text}</blockquote>
                  <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-bold text-secondary-800">
                      {item.name.slice(0, 1)}
                    </span>
                    <span>
                      <span className="block font-heading text-sm font-bold text-foreground-950">{item.name}</span>
                      <span className="block text-xs text-foreground-500">{item.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="border-t border-background-200 bg-background-100 scroll-mt-24">
          <div className="container-x py-14 md:py-20">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <p className="text-xs font-medium tracking-[0.2em] text-primary-600">CONTACT / 联系我们</p>
                <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  想聊一条线索，或只是提个建议
                </h2>
                <p className="mt-4 text-sm leading-7 text-foreground-600">
                  我们对内容方向的意见一直很欢迎。如果只是想指出某篇复盘的疏漏，也请直接写邮件，我们会认真看。
                </p>

                <ul className="mt-7 space-y-4">
                  <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-mail-line text-lg"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">邮箱</span>
                      <span className="mt-1 block text-sm font-medium text-foreground-900">{siteMeta.email}</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                      <i className="ri-wechat-line text-lg"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">微信</span>
                      <span className="mt-1 block text-sm font-medium text-foreground-900">{siteMeta.wechat}</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                      <i className="ri-map-pin-line text-lg"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">团队所在地</span>
                      <span className="mt-1 block text-sm font-medium text-foreground-900">{siteMeta.city}</span>
                    </span>
                  </li>
                </ul>
              </div>

              <div className="lg:col-span-7">
                <TipForm />
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}